import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Eye, 
  Search, 
  Filter,
  Lightbulb,
  Puzzle,
  AlertTriangle,
  Users,
  Star,
  BarChart3
} from 'lucide-react';
import { Button, Input, Select, Modal, Form, Card, Tooltip, Tag } from 'antd';
import { getGames, createGame, updateGame, deleteGame } from '../../services/gamesAPI';
import toast from 'react-hot-toast';

const { Search: AntSearch } = Input;
const { Option } = Select;
const { TextArea } = Input;

const GameManagement = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    difficulty: 'all',
  });

  const [form] = Form.useForm();

  useEffect(() => {
    fetchGames();
  }, [filters]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await getGames({
        type: filters.type === 'all' ? undefined : filters.type,
        difficulty: filters.difficulty === 'all' ? undefined : filters.difficulty,
      });

      let filteredGames = response;
      if (filters.search) {
        filteredGames = response.filter(game => 
          game.title.toLowerCase().includes(filters.search.toLowerCase()) ||
          game.description.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      setGames(filteredGames);
    } catch (error) {
      console.error('Error fetching games:', error);
      toast.error('Failed to fetch games');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGame = () => {
    setEditingGame(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleEditGame = (game) => {
    setEditingGame(game);
    setModalVisible(true);
    form.setFieldsValue(game);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      if (editingGame) {
        await updateGame(editingGame._id, values);
        toast.success('Game updated successfully');
      } else {
        await createGame(values);
        toast.success('Game created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      fetchGames();
    } catch (error) {
      console.error('Error saving game:', error);
      toast.error(error.response?.data?.message || 'Failed to save game');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    Modal.confirm({
      title: 'Delete Game',
      content: 'Are you sure you want to delete this game? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteGame(gameId);
          toast.success('Game deleted successfully');
          fetchGames();
        } catch (error) {
          console.error('Error deleting game:', error);
          toast.error(error.response?.data?.message || 'Failed to delete game');
        }
      },
    });
  };

  const getGameIcon = (type) => {
    switch (type) {
      case 'quiz':
        return <Lightbulb className="h-5 w-5 text-blue-600" />;
      case 'spot-hazard':
        return <Puzzle className="h-5 w-5 text-green-600" />;
      case 'safety-scenario':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      default:
        return <Eye className="h-5 w-5 text-gray-600" />;
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'green';
      case 'medium':
        return 'orange';
      case 'hard':
        return 'red';
      default:
        return 'gray';
    }
  };

  if (loading && games.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Game Management</h1>
            <p className="text-gray-600 mt-2">
              Create and manage safety games for your plant
            </p>
          </div>
          
          <Button 
            type="primary" 
            size="large" 
            icon={<Plus size={18} />}
            onClick={handleCreateGame}
          >
            Create New Game
          </Button>
        </div>

        {/* Filters */}
        <Card className="shadow-sm">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <span className="font-medium text-gray-700">Filters:</span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <AntSearch
                placeholder="Search games..."
                onSearch={(value) => setFilters(prev => ({ ...prev, search: value }))}
                className="max-w-md"
                enterButton
              />
              
              <Select
                value={filters.type}
                onChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
                className="w-40"
              >
                <Option value="all">All Types</Option>
                <Option value="quiz">Quiz</Option>
                <Option value="spot-hazard">Spot Hazard</Option>
                <Option value="safety-scenario">Safety Scenario</Option>
              </Select>

              <Select
                value={filters.difficulty}
                onChange={(value) => setFilters(prev => ({ ...prev, difficulty: value }))}
                className="w-40"
              >
                <Option value="all">All Difficulties</Option>
                <Option value="easy">Easy</Option>
                <Option value="medium">Medium</Option>
                <Option value="hard">Hard</Option>
              </Select>
            </div>
          </div>
        </Card>

        {/* Games Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {games.map((game) => (
            <Card
              key={game._id}
              className="shadow-md hover:shadow-lg transition-shadow duration-200"
        
            >
              <div className="space-y-4">
                {/* Game Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-gray-100 p-2 rounded-lg">
                      {getGameIcon(game.type)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">
                        {game.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Tag color={getDifficultyColor(game.difficulty)}>
                          {game.difficulty}
                        </Tag>
                        <Tag>{game.type.replace('-', ' ')}</Tag>
                      </div>
                    </div>
                  </div>
                  
                  <div className='flex items-center gap-2'>
                  <Tooltip title="Edit Game">
                  <Edit2 
                    size={16} 
                    className="text-green-600 cursor-pointer"
                    onClick={() => handleEditGame(game)}
                  />
                </Tooltip>
                <Tooltip title="Delete Game">
                  <Trash2 
                    size={16} 
                    className="text-red-600 cursor-pointer"
                    onClick={() => handleDeleteGame(game._id)}
                  />
                </Tooltip>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    game.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {game.isActive ? 'Active' : 'Inactive'}
                  </div>
                  </div>
                </div>

                {/* Game Info */}
                <div className="space-y-3">
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {game.description}
                  </p>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm flex items-center">
                      <Star size={14} className="mr-1" />
                      Points Reward
                    </span>
                    <span className="font-medium text-sm">
                      {game.pointsReward}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm flex items-center">
                      <Users size={14} className="mr-1" />
                      Total Plays
                    </span>
                    <span className="font-medium text-sm">
                      {game.totalPlays || 0}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm flex items-center">
                      <BarChart3 size={14} className="mr-1" />
                      Avg Score
                    </span>
                    <span className="font-medium text-sm">
                      {game.averageScore || 0}%
                    </span>
                  </div>
                </div>

                {/* Footer */}
                <div className="pt-2 border-t text-xs text-gray-400">
                  Created: {new Date(game.createdAt).toLocaleDateString()}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {games.length === 0 && !loading && (
          <div className="text-center py-12">
            <Lightbulb size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No games found</h3>
            <p className="text-gray-500 mb-4">
              {filters.search || filters.type !== 'all' || filters.difficulty !== 'all'
                ? 'No games match your current filters.' 
                : 'Get started by creating your first safety game.'
              }
            </p>
            <Button type="primary" icon={<Plus size={16} />} onClick={handleCreateGame}>
              Create New Game
            </Button>
          </div>
        )}

        {/* Game Form Modal */}
        <Modal
          title={editingGame ? 'Edit Game' : 'Create New Game'}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          footer={null}
          width={800}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Game Title"
                name="title"
                rules={[{ required: true, message: 'Please enter game title' }]}
              >
                <Input placeholder="Enter game title" />
              </Form.Item>

              <Form.Item
                label="Game Type"
                name="type"
                rules={[{ required: true, message: 'Please select game type' }]}
              >
                <Select placeholder="Select game type">
                  <Option value="quiz">Quiz</Option>
                  <Option value="spot-hazard">Spot Hazard</Option>
                  <Option value="safety-scenario">Safety Scenario</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: 'Please enter description' }]}
            >
              <TextArea 
                rows={3} 
                placeholder="Enter game description" 
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Difficulty"
                name="difficulty"
                rules={[{ required: true, message: 'Please select difficulty' }]}
              >
                <Select placeholder="Select difficulty">
                  <Option value="easy">Easy</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="hard">Hard</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Points Reward"
                name="pointsReward"
                rules={[{ required: true, message: 'Please enter points reward' }]}
              >
                <Input type="number" placeholder="Enter points reward" />
              </Form.Item>
            </div>

            {/* Dynamic fields based on game type */}
            <Form.Item shouldUpdate>
              {({ getFieldValue }) => {
                const gameType = getFieldValue('type');
                
                if (gameType === 'quiz') {
                  return (
                    <Form.List name="questions">
                      {(fields, { add, remove }) => (
                        <div>
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-medium">Quiz Questions</h4>
                            <Button type="dashed" onClick={() => add()}>
                              Add Question
                            </Button>
                          </div>
                          {fields.map(({ key, name, ...restField }) => (
                            <Card key={key} className="mb-4">
                              <Form.Item
                                {...restField}
                                name={[name, 'question']}
                                label="Question"
                                rules={[{ required: true, message: 'Please enter question' }]}
                              >
                                <Input placeholder="Enter question" />
                              </Form.Item>
                              
                              <Form.List name={[name, 'options']}>
                                {(optionFields, { add: addOption, remove: removeOption }) => (
                                  <div>
                                    <div className="flex justify-between items-center mb-2">
                                      <label className="font-medium">Options</label>
                                      <Button size="small" type="dashed" onClick={() => addOption()}>
                                        Add Option
                                      </Button>
                                    </div>
                                    {optionFields.map(({ key: optionKey, name: optionName, ...restOptionField }) => (
                                      <div key={optionKey} className="flex gap-2 mb-2">
                                        <Form.Item
                                          {...restOptionField}
                                          name={optionName}
                                          className="flex-1 mb-0"
                                        >
                                          <Input placeholder="Enter option" />
                                        </Form.Item>
                                        <Button 
                                          size="small" 
                                          danger 
                                          onClick={() => removeOption(optionName)}
                                        >
                                          Remove
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </Form.List>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Form.Item
                                  {...restField}
                                  name={[name, 'correctAnswer']}
                                  label="Correct Answer"
                                  rules={[{ required: true, message: 'Please enter correct answer' }]}
                                >
                                  <Input placeholder="Enter correct answer" />
                                </Form.Item>

                                <Form.Item
                                  {...restField}
                                  name={[name, 'points']}
                                  label="Points"
                                  rules={[{ required: true, message: 'Please enter points' }]}
                                >
                                  <Input type="number" placeholder="Points for this question" />
                                </Form.Item>
                              </div>

                              <Button danger onClick={() => remove(name)}>
                                Remove Question
                              </Button>
                            </Card>
                          ))}
                        </div>
                      )}
                    </Form.List>
                  );
                }

                if (gameType === 'spot-hazard') {
                  return (
                    <div>
                      <Form.Item
                        label="Hazard Image URL"
                        name="hazardImage"
                        rules={[{ required: true, message: 'Please enter image URL' }]}
                      >
                        <Input placeholder="Enter image URL" />
                      </Form.Item>

                      <Form.List name="hazards">
                        {(fields, { add, remove }) => (
                          <div>
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="font-medium">Hazards</h4>
                              <Button type="dashed" onClick={() => add()}>
                                Add Hazard
                              </Button>
                            </div>
                            {fields.map(({ key, name, ...restField }) => (
                              <Card key={key} className="mb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'description']}
                                    label="Description"
                                    rules={[{ required: true, message: 'Please enter description' }]}
                                  >
                                    <Input placeholder="Hazard description" />
                                  </Form.Item>

                                  <Form.Item
                                    {...restField}
                                    name={[name, 'points']}
                                    label="Points"
                                    rules={[{ required: true, message: 'Please enter points' }]}
                                  >
                                    <Input type="number" placeholder="Points" />
                                  </Form.Item>

                                  <Form.Item
                                    {...restField}
                                    name={[name, 'x']}
                                    label="X Position (%)"
                                    rules={[{ required: true, message: 'Please enter X position' }]}
                                  >
                                    <Input type="number" placeholder="X coordinate" />
                                  </Form.Item>

                                  <Form.Item
                                    {...restField}
                                    name={[name, 'y']}
                                    label="Y Position (%)"
                                    rules={[{ required: true, message: 'Please enter Y position' }]}
                                  >
                                    <Input type="number" placeholder="Y coordinate" />
                                  </Form.Item>
                                </div>

                                <Button danger onClick={() => remove(name)}>
                                  Remove Hazard
                                </Button>
                              </Card>
                            ))}
                          </div>
                        )}
                      </Form.List>
                    </div>
                  );
                }

                if (gameType === 'safety-scenario') {
                  return (
                    <div>
                      <Form.Item
                        label="Scenario"
                        name="scenario"
                        rules={[{ required: true, message: 'Please enter scenario' }]}
                      >
                        <TextArea rows={3} placeholder="Enter safety scenario" />
                      </Form.Item>

                      <Form.List name="scenarioOptions">
                        {(fields, { add, remove }) => (
                          <div>
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="font-medium">Scenario Options</h4>
                              <Button type="dashed" onClick={() => add()}>
                                Add Option
                              </Button>
                            </div>
                            {fields.map(({ key, name, ...restField }) => (
                              <Card key={key} className="mb-4">
                                <Form.Item
                                  {...restField}
                                  name={[name, 'text']}
                                  label="Option Text"
                                  rules={[{ required: true, message: 'Please enter option text' }]}
                                >
                                  <TextArea rows={2} placeholder="Enter option text" />
                                </Form.Item>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'isCorrect']}
                                    label="Is Correct?"
                                    valuePropName="checked"
                                  >
                                    <Select>
                                      <Option value={true}>Yes</Option>
                                      <Option value={false}>No</Option>
                                    </Select>
                                  </Form.Item>

                                  <Form.Item
                                    {...restField}
                                    name={[name, 'points']}
                                    label="Points"
                                    rules={[{ required: true, message: 'Please enter points' }]}
                                  >
                                    <Input type="number" placeholder="Points" />
                                  </Form.Item>
                                </div>

                                <Form.Item
                                  {...restField}
                                  name={[name, 'feedback']}
                                  label="Feedback"
                                  rules={[{ required: true, message: 'Please enter feedback' }]}
                                >
                                  <TextArea rows={2} placeholder="Enter feedback" />
                                </Form.Item>

                                <Button danger onClick={() => remove(name)}>
                                  Remove Option
                                </Button>
                              </Card>
                            ))}
                          </div>
                        )}
                      </Form.List>
                    </div>
                  );
                }

                return null;
              }}
            </Form.Item>

            {/* Form Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button 
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
              >
                {editingGame ? 'Update Game' : 'Create Game'}
              </Button>
            </div>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default GameManagement;